import { scaleSqrt } from 'd3';
import { extent } from 'd3-array';
import { useMemo } from 'react';

import {
  type GetSentimentMapDataQuery,
  type MapCoordsGranularity,
  SENTIMENT_GET_MAP_DATA,
} from '@revelio/data-access';

import { useSelectionListsForKeys } from '../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { useAvailableDateRangeWithTrialLag } from '../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { getFormattedDateString } from '../../filters/utils/dateUtils';
import { getDim1 } from '../../filters/utils/getDim1';
import { getSentimentV2Filters } from '../../filters/utils/getSentimentV2Filters';
import type { LocationType, MapPoint } from '../Chart/Map/types';
import { MAX_RADIUS, MIN_RADIUS } from '../Chart/Map/utils/constants';
import { determineLocationType } from '../Chart/Map/utils/location-type-utils';
import type { SentimentMetricValues } from '../PageConfigProvider/types/metric';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import {
  extractGranularitiesWithIds,
  useCoordinateResolverForMixedGranularities,
} from './utils/coordinate-resolver';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from './utils/data-provider-results-utils';

type SentimentMapDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  metric: SentimentMetricValues;
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
};

export const SentimentMapDataProvider = ({
  primaryDimension,
  metric,
  children,
}: SentimentMapDataProviderProps) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListsForKeys(['country', 'msa']);
  const { country: countryList, msa: msaList } = selectionLists;
  const sentimentFilters = getSentimentV2Filters(effectiveFilters);
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const snapshotDate = getFormattedDateString(
    availableDateRange?.month.max,
    'month'
  );
  const dim1 = getDim1(primaryDimension);

  // Restrict to Geography primary filter
  const isGeographyView = primaryDimension === FilterId.GEOGRAPHY;

  // Create filter configurations for dual requests when countries are selected
  const { countryFilters, msaFilters, needsDualRequest } = useMemo(() => {
    // Wait until both universe lists have loaded before enriching — they
    // arrive together from one geo-tree fetch, and enriching with a half-loaded
    // universe would send the MSA query with an empty id list.
    if (!countryList || !msaList) {
      return {
        countryFilters: {
          filters: {
            ...sentimentFilters,
            start_date: snapshotDate,
            end_date: snapshotDate,
          },
          dim1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }

    const { region, country, msa } = sentimentFilters;

    // If no geo filters are selected, use all countries
    if (!region?.length && !country?.length && !msa?.length) {
      const enrichedCountry = countryList.map((c) => Number(c.id));
      return {
        countryFilters: {
          filters: {
            ...sentimentFilters,
            region: [],
            country: enrichedCountry,
            msa: [],
            start_date: snapshotDate,
            end_date: snapshotDate,
          },
          dim1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }
    // If only countries are selected, we need dual requests
    else if (country?.length && !msa?.length) {
      const enrichedMsa = (msaList ?? [])
        .filter((m) => country.includes(Number(m.country)))
        .map((m) => Number(m.id));

      return {
        countryFilters: {
          filters: {
            ...sentimentFilters,
            region: [],
            country: country,
            msa: [],
            start_date: snapshotDate,
            end_date: snapshotDate,
          },
          dim1,
        },
        msaFilters: {
          filters: {
            ...sentimentFilters,
            region: [],
            country: [],
            msa: enrichedMsa,
            start_date: snapshotDate,
            end_date: snapshotDate,
          },
          dim1,
        },
        needsDualRequest: true,
      };
    }
    // For other cases (regions or MSAs selected), use single request
    else {
      return {
        countryFilters: {
          filters: {
            ...sentimentFilters,
            start_date: snapshotDate,
            end_date: snapshotDate,
          },
          dim1,
        },
        msaFilters: null,
        needsDualRequest: false,
      };
    }
  }, [sentimentFilters, countryList, msaList, snapshotDate, dim1]);

  // First request (always made)
  const [{ data: countryData, status: countryStatus }] =
    useUrqlQueryWithStatus<GetSentimentMapDataQuery>({
      query: SENTIMENT_GET_MAP_DATA,
      variables: countryFilters,
      pause: !dim1 || !isGeographyView,
    });

  // Second request (only when dual request is needed)
  const [{ data: msaData, status: msaStatus }] =
    useUrqlQueryWithStatus<GetSentimentMapDataQuery>({
      query: SENTIMENT_GET_MAP_DATA,
      variables: msaFilters || undefined,
      pause: !needsDualRequest || !msaFilters || !dim1 || !isGeographyView,
    });

  // Combine status and data from both requests
  const status = needsDualRequest
    ? combineQueryStatus([countryStatus, msaStatus])
    : countryStatus;

  const data = useMemo(() => {
    if (!needsDualRequest) {
      return countryData;
    }

    // Merge data from both requests
    if (!countryData?.sentiment || !msaData?.sentiment) {
      return countryData || msaData;
    }

    // Combine entities from both requests
    const combinedEntities = [...countryData.sentiment, ...msaData.sentiment];

    return {
      ...countryData,
      sentiment: combinedEntities,
    };
  }, [countryData, msaData, needsDualRequest]);

  const entities = useMemo(() => data?.sentiment ?? [], [data?.sentiment]);

  const metricKey = metric === 'sentiment_top_bottom' ? 'overall' : metric;

  const geoData = useMemo(() => {
    return entities
      .map((entity) => {
        const id = Number(entity?.metadata?.id ?? 0);
        const label =
          entity?.metadata?.shortName || entity?.metadata?.longName || '';
        const series = (
          entity?.result?.scores as Record<string, Array<{ value?: number }>>
        )[metricKey] as Array<{ value?: number }> | undefined;
        const last =
          series && series.length ? series[series.length - 1] : undefined;
        const value = last?.value ?? null;
        if (!id || value == null) return null;
        return { id, label, value: Number(value) };
      })
      .filter(
        (d): d is { id: number; label: string; value: number } => d !== null
      );
  }, [entities, metricKey]);

  const granularitiesWithIds = useMemo(() => {
    return extractGranularitiesWithIds(geoData);
  }, [geoData]);

  const { data: coords, loading: coordsLoading } =
    useCoordinateResolverForMixedGranularities(granularitiesWithIds);

  const points = useMemo((): MapPoint[] => {
    if (!coords.length || !geoData.length) return [];
    const coordMap = new Map(coords.map((c) => [c.id, c] as const));
    const filtered = geoData.filter((g) => coordMap.has(g.id));

    const ext = extent(filtered.map((d) => d.value));
    const [minVal, maxVal] = ext as [number | undefined, number | undefined];
    if (minVal == null || maxVal == null) return [];
    const domainMax = maxVal === minVal ? minVal + 1 : maxVal;
    const scale = scaleSqrt()
      .domain([minVal, domainMax] as [number, number])
      .range([MIN_RADIUS, MAX_RADIUS]);

    const getLocationType = (
      label: string,
      coordGranularity: MapCoordsGranularity
    ): LocationType => {
      return determineLocationType(label, coordGranularity);
    };

    return filtered
      .map((d) => {
        const c = coordMap.get(d.id);
        if (!c) return null;
        const label = d.label || c.name;
        return {
          id: String(d.id),
          label,
          lat: c.lat,
          lon: c.lon,
          value: d.value / 100,
          circleRadius: scale(d.value),
          locationType: getLocationType(label, c.granularity),
        } as MapPoint;
      })
      .filter((p): p is MapPoint => p !== null);
  }, [coords, geoData]);

  const finalStatus = coordsLoading
    ? DataProviderQueryStatus.Fetching
    : (status ?? DataProviderQueryStatus.Idle);

  return children({ data: { points }, status: finalStatus });
};
